/**
 * Quota-safe localStorage writes. A browser origin gets ~5 MB, and rebuildable
 * caches (folder listings, takeoff thumbnails, the DataService mirror) can fill
 * it on their own. When they do, a write that actually matters, such as the
 * signed-in user, must evict those caches rather than throw into the caller.
 */

// Every entry here is a cache the app rebuilds from the server on next read,
// so dropping it costs a refetch and nothing else.
const DISPOSABLE_PREFIXES = ['document-files:', 'document-folders:'];
const DISPOSABLE_KEYS = [
  'quantis.takeoffThumbnailCache.v3',
  'quantis-projects-with-boq-cache-v1',
  'quantis-boq-totals-cache-v1',
  'quantis_profit_loss_cache_v1',
  'quantis_workflows_cache_v3',
  'quantis_trigger_catalog_cache_v1',
  'contract_group_database',
];

export function isQuotaExceededError(error: unknown): boolean {
  if (!(error instanceof DOMException)) return false;
  // Firefox reports NS_ERROR_DOM_QUOTA_REACHED (1014); older WebKit uses code 22.
  return error.name === 'QuotaExceededError'
    || error.name === 'NS_ERROR_DOM_QUOTA_REACHED'
    || error.code === 22
    || error.code === 1014;
}

/** Removes every rebuildable cache entry. Returns how many keys were dropped. */
export function evictDisposableCaches(): number {
  const doomed: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;
    if (DISPOSABLE_KEYS.includes(key) || DISPOSABLE_PREFIXES.some(prefix => key.startsWith(prefix))) {
      doomed.push(key);
    }
  }
  doomed.forEach(key => localStorage.removeItem(key));
  return doomed.length;
}

/**
 * Writes `value`, evicting rebuildable caches and retrying once if the quota is
 * full. Never throws: returns false when the value still could not be stored,
 * which callers treat as "not cached" since the server remains the source of truth.
 */
export function setItemWithEviction(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    if (!isQuotaExceededError(error)) {
      console.warn(`localStorage write for "${key}" failed:`, error);
      return false;
    }
  }

  try {
    if (evictDisposableCaches() > 0) {
      localStorage.setItem(key, value);
      return true;
    }
  } catch {
    // Still full after eviction; fall through.
  }
  console.warn(`localStorage is full; "${key}" was not persisted.`);
  return false;
}
